/** @jsx jsx */
import { jsx, ThemeUIStyleObject } from "theme-ui"
import React from "react"
import {
  Accordion as ReachUIAccordion,
  AccordionProps as ReachUIAccordionProps,
  AccordionItem as ReachUIAccordionItem,
  AccordionItemProps as ReachUIAccordionItemProps,
  AccordionButton as ReachUIAccordionButton,
  AccordionButtonProps as ReachUIAccordionButtonProps,
  AccordionPanel as ReachUIAccordionPanel,
  AccordionPanelProps as ReachUIAccordionPanelProps,
} from "@reach/accordion"
import { PrimitiveBoxProps, PrimitiveProps } from "@styles/types"
import "@reach/accordion/styles.css"
import { getThemeVariantStyles } from "@styles/utils/getThemeVariant"

export interface AccordionProps extends PrimitiveProps, ReachUIAccordionProps {
  /**
   * Whether or not multiple panels in an uncontrolled accordion can be opened
   * at the same time. By default, when a user opens a new panel, the previously
   * opened panel will close. This prop prevents that behavior.
   *
   * It's important to note that this prop has no impact on controlled
   * components, since the state of any given accordion panel is managed solely
   * by the index prop.
   */
  multiple?: boolean
}

export interface AccordionItemProps
  extends PrimitiveBoxProps,
    ReachUIAccordionItemProps {
  as?: React.ElementType
}

export interface AccordionButtonProps
  extends PrimitiveProps,
    ReachUIAccordionButtonProps {}

export interface AccordionPanelProps
  extends PrimitiveProps,
    ReachUIAccordionPanelProps {}

export const accordionStyles: ThemeUIStyleObject = {}

const Accordion: React.FC<AccordionProps> = ({ sx, variant, ...props }) => (
  <ReachUIAccordion
    sx={{
      ...accordionStyles,
      ...(variant && { ...getThemeVariantStyles("accordion", variant) }),
      ...sx,
    }}
    {...props}
  />
)

Accordion.defaultProps = {}

export const accordionItemStyles: ThemeUIStyleObject = {}

const AccordionItem: React.FC<AccordionItemProps> = ({ sx, as, ...props }) => (
  <ReachUIAccordionItem
    sx={{ ...accordionItemStyles, ...sx }}
    {...props}
    as={as as React.ElementType}
  />
)

AccordionItem.defaultProps = {}

export const accordionButtonStyles: ThemeUIStyleObject = {}

const AccordionButton: React.FC<AccordionButtonProps> = ({ sx, ...props }) => (
  <ReachUIAccordionButton sx={{ ...accordionButtonStyles, ...sx }} {...props} />
)

AccordionButton.defaultProps = {}

export const accordionPanelStyles: ThemeUIStyleObject = {}

const AccordionPanel: React.FC<AccordionPanelProps> = ({ sx, ...props }) => (
  <ReachUIAccordionPanel sx={{ ...accordionPanelStyles, ...sx }} {...props} />
)

AccordionPanel.defaultProps = {}

export { Accordion, AccordionItem, AccordionButton, AccordionPanel }
